import {
  Badge,
  BadgeColor,
  BadgeContainer,
} from '@delmonicos/shared-design-system-react/src/components/badge/badge';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Button,
  ButtonColor,
} from '@delmonicos/shared-design-system-react/src/components/button/button';
import {
  Dialog,
  DialogPosition,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  Icon,
  IconColor,
  IconSize,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  ResourceStatus,
  withProtectedResource,
} from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { GetTariffsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/tariffs/get-tariffs-paginated.model';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  OnChangeFn,
  SortingState,
  functionalUpdate,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { useEmspBoGetCposQuery } from '@app/core/service/emsp-bo-cpo.service';
import { useEmspBoGetTariffsQuery } from '@app/core/service/emsp-bo-tariff.service';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';
import {
  createColumnOrderChangeHandler,
  createHideColumnHandler,
} from '@shared/list-options/column-options.helper';

import { EmspBoTariffListFilterSearchController } from '../emsp-bo-tariff-list-filter-search/emsp-bo-tariff-list-filter-search.controller';
import { EmspBoTariffListFilterStateController } from '../emsp-bo-tariff-list-filter-state/emsp-bo-tariff-list-filter-state.controller';
import { EmspBoTariffListFilterController } from '../emsp-bo-tariff-list-filter/emsp-bo-tariff-list-filter.controller';
import { EmspBoTariffListOptionsController } from '../emsp-bo-tariff-list-options/emsp-bo-tariff-list-options.controller';
import { EmspBoTariffPullCreateController } from '../emsp-bo-tariff-pull-create/emsp-bo-tariff-pull-create.controller';
import {
  EmspBoTariffListContextProvider,
  useEmspBoTariffListContext,
} from '../emsp-bo-tariff.context';
import {
  EMSP_BO_TARIFF_LIST_FILTER_NAME_LIST,
  EMSP_BO_TARIFF_LIST_SORT_FIELD,
} from './emsp-bo-tariff-list.config';
import * as css from './emsp-bo-tariff-list.module.scss';
import { EmspBoTariffListView } from './emsp-bo-tariff-list.view';

export const EmspBoTariffListController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const {
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    getPaginatedLimitList,
    columnOptions,
    setColumnOptions,
  } = useEmspBoTariffListContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionFilterIsDialogOpen, setActionFilterIsDialogOpen] =
    useState(false);
  const [actionPullIsDialogOpen, setActionPullIsDialogOpen] = useState(false);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const { data: cposData } = useEmspBoGetCposQuery({ limit: 100, offset: 0 });

  useEffect(() => {
    const firstCpoId = cposData?.docs.at(0)?.id;
    if (firstCpoId && !getPaginatedQueryParameters.cpoId) {
      setGetPaginatedQueryParameters((current) => ({
        ...current,
        cpoId: firstCpoId,
      }));
    }
  }, [
    cposData,
    getPaginatedQueryParameters.cpoId,
    setGetPaginatedQueryParameters,
  ]);

  const hasCpoId = getPaginatedQueryParameters.cpoId !== '';
  const queryState = useEmspBoGetTariffsQuery(getPaginatedQueryParameters, {
    skip: !hasCpoId,
  });

  const paginationState = useMemo(
    () =>
      getState({
        isLoading: queryState.isLoading || queryState.isUninitialized,
        isError: queryState.isError,
        isFetching: queryState.isFetching,
      }),
    [
      queryState.isLoading,
      queryState.isUninitialized,
      queryState.isError,
      queryState.isFetching,
    ],
  );

  const filterNameActiveList = EMSP_BO_TARIFF_LIST_FILTER_NAME_LIST.filter(
    (filterName) => getPaginatedQueryParameters[filterName] !== undefined,
  );

  const filterActiveHiddenCount = Math.max(
    0,
    filterNameActiveList.reduce((count, filterName) => {
      const filterValue = getPaginatedQueryParameters[filterName] as any;

      if (Array.isArray(filterValue)) {
        return count + filterValue.length;
      }

      return count + 1;
    }, 0) - 3,
  );

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    if (
      hasCpoId &&
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.EMSP_BO,
        action: ActionsByResource.EMSP_BO.PULL_TARIFFS,
      })
    ) {
      actions.push(
        <Button
          key="pull"
          color={ButtonColor.Primary}
          onClick={() => setActionPullIsDialogOpen(true)}>
          <Stack gap={GapEnum.S} alignItems="center">
            <Icon
              name="refresh-cw-01"
              color={IconColor.Inherit}
              iconSize={IconSize.Small}
            />
            {t('$.emsp-bo-tariff-pull-create.button')}
          </Stack>
        </Button>,
      );
    }

    setPageAction(actions);
  }, [setPageAction, userPermissions, t, hasCpoId]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handlePaginationPageChange = (paginationPageNext: number): void => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      offset: (paginationPageNext - 1) * current.limit,
    }));
  };

  const handlePaginationLimitChange = (paginationLimitNext: number): void => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      limit: paginationLimitNext,
      offset: 0,
    }));
  };

  const handlePaginationSortingChange: OnChangeFn<SortingState> = (
    sortingState,
  ): void => {
    setGetPaginatedQueryParameters((current) => {
      const paginationSorting0 = functionalUpdate(sortingState, [
        {
          desc: current.sortOrder === -1,
          id: current.sortField ?? EMSP_BO_TARIFF_LIST_SORT_FIELD,
        },
      ]).at(0);

      return {
        ...current,
        sortField:
          paginationSorting0?.id as GetTariffsPaginatedModel['sortField'],
        sortOrder: paginationSorting0?.desc ? -1 : 1,
      };
    });
  };

  const handleHideColumn = createHideColumnHandler(setColumnOptions);
  const handleColumnOrderChange =
    createColumnOrderChangeHandler(setColumnOptions);

  // RENDER ////////////////////////////////////////////////////////////////////

  const { Component: EmspBoTariffListViewProtected, status: resourceStatus } =
    withProtectedResource(EmspBoTariffListView, queryState, {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.EMSP_BO,
          action: ActionsByResource.EMSP_BO.READ_TARIFF,
        }),
      transformDataToResource: transformPaginatedResponse,
    })({
      paginationLimit: getPaginatedQueryParameters.limit,
      paginationLimitList: getPaginatedLimitList,
      paginationState,
      paginationSorting: [
        {
          desc: getPaginatedQueryParameters.sortOrder === -1,
          id:
            getPaginatedQueryParameters.sortField ??
            EMSP_BO_TARIFF_LIST_SORT_FIELD,
        },
      ],
      columnVisibility: columnOptions.columnVisibility,
      columnOrder: columnOptions.columnOrder,
      renderColumnSettings: () => <EmspBoTariffListOptionsController />,
      onPaginationLimitChange: handlePaginationLimitChange,
      onPaginationPageChange: handlePaginationPageChange,
      onPaginationSortingChange: handlePaginationSortingChange,
      onHideColumn: handleHideColumn,
      onColumnOrderChange: handleColumnOrderChange,
    });

  return (
    <Stack flex={1} className={css.listContainer} direction="column">
      {resourceStatus !== ResourceStatus.UNAUTHORIZED && (
        <Stack direction="row" justifyContent="space-between" gap={GapEnum.XL}>
          <Stack flex={1}>
            <EmspBoTariffListFilterSearchController
              onCancel={() => {}}
              onSuccess={() => {}}
            />
          </Stack>
          <EmspBoTariffListFilterStateController />
          <Stack justifyContent="end" gap={GapEnum.M}>
            <BadgeContainer
              badge={
                filterActiveHiddenCount > 0 && (
                  <Badge color={BadgeColor.Secondary}>
                    {`+${filterActiveHiddenCount}`}
                  </Badge>
                )
              }>
              <ButtonIcon
                icon="filter-funnel-01"
                onClick={() => setActionFilterIsDialogOpen(true)}
              />
            </BadgeContainer>
          </Stack>
        </Stack>
      )}
      <Stack className={css.tableContainer}>
        {EmspBoTariffListViewProtected}
      </Stack>
      {resourceStatus !== ResourceStatus.UNAUTHORIZED && (
        <>
          <Dialog
            open={actionFilterIsDialogOpen}
            onClose={() => setActionFilterIsDialogOpen(false)}
            position={DialogPosition.RIGHT}>
            <Dialog.Header
              icon="filter-funnel-01"
              title={t('$.emsp-bo-tariff-list-filter.title')}
              onClose={() => setActionFilterIsDialogOpen(false)}
            />
            <Dialog.Content>
              <EmspBoTariffListFilterController
                onCancel={() => setActionFilterIsDialogOpen(false)}
                onSuccess={() => setActionFilterIsDialogOpen(false)}
              />
            </Dialog.Content>
          </Dialog>
          <Dialog
            open={actionPullIsDialogOpen}
            onClose={() => setActionPullIsDialogOpen(false)}
            position={DialogPosition.RIGHT}>
            <Dialog.Header
              icon="refresh-cw-01"
              title={t('$.emsp-bo-tariff-pull-create.title')}
              onClose={() => setActionPullIsDialogOpen(false)}
            />
            <Dialog.Content>
              <EmspBoTariffPullCreateController
                cpoId={getPaginatedQueryParameters.cpoId}
                onCancel={() => setActionPullIsDialogOpen(false)}
                onSuccess={() => setActionPullIsDialogOpen(false)}
              />
            </Dialog.Content>
          </Dialog>
        </>
      )}
    </Stack>
  );
};

export const EmspBoTariffListControllerWithContext = (): React.JSX.Element => {
  return (
    <EmspBoTariffListContextProvider>
      <EmspBoTariffListController />
    </EmspBoTariffListContextProvider>
  );
};
